import { useState, type CSSProperties } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import SaveAnimatedIcon from '@/components/ui/SaveAnimatedIcon';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import { createApiKey, sendApiWelcomeEmail } from './api-keys.service';
import {
  PRESET_SCOPES,
  PRESET_LABELS,
  PRESET_DESCRIPTIONS,
} from './presets';
import type { ScopePreset, CreatedKey } from './types';

// ── Constants ────────────────────────────────────────────────────────────────

const RESOURCES = ['pessoa', 'obra', 'atividade', 'os'] as const;
type Resource = typeof RESOURCES[number];
const ACTIONS = ['read', 'create', 'update', 'delete'] as const;
type Action = typeof ACTIONS[number];
const ACTION_LABELS: Record<Action, string> = {
  read: 'Ler',
  create: 'Criar',
  update: 'Editar',
  delete: 'Excluir',
};
const RESOURCE_LABELS: Record<Resource, string> = {
  pessoa: 'Pessoas',
  obra: 'Obras',
  atividade: 'Atividades',
  os: 'OS',
};

const PRESETS: Exclude<ScopePreset, 'custom'>[] = [
  'read-only',
  'read-create',
  'read-edit',
  'full',
];

// ── Styles ───────────────────────────────────────────────────────────────────

const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  zIndex: 9900,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
};

const PANEL_STYLE: CSSProperties = {
  width: '420px',
  maxWidth: '100vw',
  height: '100vh',
  background: 'var(--panel-bg, #fff)',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  overflowY: 'auto',
};

const HEADER_STYLE: CSSProperties = {
  padding: '20px 24px 16px',
  borderBottom: '1px solid var(--panel-border, #e5e7eb)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  background: '#f8faff',
};

const BODY_STYLE: CSSProperties = {
  padding: '24px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const FOOTER_STYLE: CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid var(--panel-border, #e5e7eb)',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  flexShrink: 0,
  background: '#f9fafb',
};

const LABEL_STYLE: CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
};

const INPUT_STYLE: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  background: 'white',
};

// ── Props ────────────────────────────────────────────────────────────────────

export interface CreateApiKeyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (result: CreatedKey) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function CreateApiKeyDrawer({
  isOpen,
  onClose,
  onCreated,
}: CreateApiKeyDrawerProps) {
  const { user, pessoa } = useAuth();
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<ScopePreset>('read-edit');
  const [customScopes, setCustomScopes] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const resolvedScopes: string[] =
    preset === 'custom'
      ? Array.from(customScopes)
      : PRESET_SCOPES[preset];

  const isDisabled =
    submitting || name.trim() === '' || resolvedScopes.length === 0;

  const toggleCustomScope = (scope: string) => {
    setCustomScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) {
        next.delete(scope);
      } else {
        next.add(scope);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isDisabled) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createApiKey({
        name: name.trim(),
        scopes: resolvedScopes,
      });

      // Fire-and-forget welcome email. The edge function decides authoritatively
      // whether to actually send (counts api_key rows for the pessoa, including
      // revoked, and sends only when count === 1). Errors must never block the
      // post-creation modal — losing a welcome email is worse than losing the
      // raw key, which is shown ONCE on the next render.
      if (user?.email) {
        const userName = (pessoa?.nome ?? '').trim() || undefined;
        void sendApiWelcomeEmail({
          userEmail: user.email,
          userName,
          keyName: name.trim(),
        }).catch((welcomeErr) => {
          console.warn('[api-keys] welcome email dispatch failed:', welcomeErr);
        });
      }

      // Reset form
      setName('');
      setPreset('read-edit');
      setCustomScopes(new Set());
      onCreated(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar chave.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setName('');
    setPreset('read-edit');
    setCustomScopes(new Set());
    setError(null);
    onClose();
  };

  return createPortal(
    <div style={BACKDROP_STYLE} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={PANEL_STYLE} role="dialog" aria-modal="true" aria-label="Nova chave de API">

        {/* Header */}
        <div style={HEADER_STYLE}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#154680' }}>
              Nova chave de API
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
              Configure nome e permissões de acesso
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280',
              borderRadius: '4px',
            }}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={BODY_STYLE}>
          {/* Name field */}
          <div>
            <label htmlFor="api-key-name" style={LABEL_STYLE}>
              Nome da chave <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              id="api-key-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex: Zapier integração produção"
              style={INPUT_STYLE}
              disabled={submitting}
              autoFocus
              maxLength={100}
            />
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' }}>
              Use um nome descritivo para identificar a integração que vai usar esta chave.
            </p>
          </div>

          {/* Preset selection */}
          <div>
            <label style={LABEL_STYLE}>
              Permissões de acesso <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {PRESETS.map((p) => {
                const isSelected = preset === p;
                return (
                  <label
                    key={p}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '10px 12px',
                      border: `1.5px solid ${isSelected ? '#154680' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: isSelected ? '#f0f5ff' : 'white',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="scope-preset"
                      value={p}
                      checked={isSelected}
                      onChange={() => setPreset(p)}
                      disabled={submitting}
                      style={{ marginTop: '2px', accentColor: '#154680' }}
                    />
                    <div>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: isSelected ? '#154680' : '#111827',
                      }}>
                        {PRESET_LABELS[p]}
                      </span>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
                        {PRESET_DESCRIPTIONS[p]}
                      </p>
                    </div>
                  </label>
                );
              })}

              {/* Custom preset */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  border: `1.5px solid ${preset === 'custom' ? '#154680' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: preset === 'custom' ? '#f0f5ff' : 'white',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="scope-preset"
                  value="custom"
                  checked={preset === 'custom'}
                  onChange={() => setPreset('custom')}
                  disabled={submitting}
                  style={{ marginTop: '2px', accentColor: '#154680' }}
                />
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: preset === 'custom' ? '#154680' : '#111827',
                  }}>
                    Personalizado
                  </span>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
                    Escolha exatamente quais recursos e ações esta chave pode usar.
                  </p>

                  {/* Custom scope grid */}
                  {preset === 'custom' && (
                    <div style={{ marginTop: '12px' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '12px',
                      }}>
                        <thead>
                          <tr>
                            <th style={{
                              textAlign: 'left',
                              padding: '4px 0',
                              color: '#6b7280',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}>
                              Recurso
                            </th>
                            {ACTIONS.map((a) => (
                              <th key={a} style={{
                                textAlign: 'center',
                                padding: '4px 8px',
                                color: '#6b7280',
                                fontWeight: 600,
                                fontSize: '11px',
                                textTransform: 'uppercase',
                              }}>
                                {ACTION_LABELS[a]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {RESOURCES.map((resource) => (
                            <tr key={resource} style={{ borderTop: '1px solid #f3f4f6' }}>
                              <td style={{
                                padding: '6px 0',
                                fontWeight: 500,
                                color: '#374151',
                              }}>
                                {RESOURCE_LABELS[resource]}
                              </td>
                              {ACTIONS.map((action) => {
                                const scope = `${resource}:${action}`;
                                return (
                                  <td key={action} style={{ textAlign: 'center', padding: '6px 8px' }}>
                                    <input
                                      type="checkbox"
                                      checked={customScopes.has(scope)}
                                      onChange={() => toggleCustomScope(scope)}
                                      disabled={submitting}
                                      style={{ accentColor: '#154680', cursor: 'pointer' }}
                                      aria-label={`${resource} ${action}`}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {customScopes.size === 0 && (
                        <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#f59e0b' }}>
                          Selecione ao menos uma permissão.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              padding: '10px 12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={FOOTER_STYLE}>
          <button
            className="btn-add undo-animated"
            onClick={handleClose}
            disabled={submitting}
          >
            <UndoAnimatedIcon size={15} />
            <span className="btn-text">Cancelar</span>
          </button>
          <button
            className="btn-add save-animated"
            onClick={handleSubmit}
            disabled={isDisabled}
          >
            {submitting ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(21,70,128,0.3)',
                  borderTop: '2px solid #154680',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  flexShrink: 0,
                }} />
                <span className="btn-text">Criando...</span>
              </>
            ) : (
              <>
                <SaveAnimatedIcon size={16} />
                <span className="btn-text">Criar chave</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
